import React, {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {KatButton, KatDropdown, KatInput, KatModal, KatTextarea} from "@amzn/katal-react";
import {DateTimePicker} from "src/components/common/DateTimePicker";
import {
    constructEvent,
    constructFormData,
    getElementsDataFromRefs,
    isDateRangeFromRefsValid,
    isElementsFromRefsValid,
    resetFormData
} from "src/common/utils/formUtils";
import {IEvent} from "src/context/models/Event";
import {
    DESCRIPTION_ID,
    END_ID,
    FIELD_CLASS_NAME,
    SEVERITY_ID,
    START_ID,
    SUMMARY_ID
} from "src/common/constants/formConstants";
import {EVENT_SEVERITY, HEALTH_EVENT} from "src/common/constants/constants";
import {deleteEventAction, postEventAction} from "src/context/actions/EventActions";
import {GlobalContext} from "src/context/GlobalProvider";

export type IEventModalProps = {
    setVisible: (visible: boolean) => void;
    title?: string;
    section?: string;
    operation?: string;
    event?: IEvent;
};

export function EventModal(props: IEventModalProps) {
    const {setVisible, title, section, operation, event} = props;
    const [isSubmitting, setSubmittingStatus] = useState(false);
    const [isDeleting, setDeletingStatus] = useState(false);
    const {eventDispatch, sessionState} = useContext(GlobalContext);
    const region = sessionState.data.region;
    const {t} = useTranslation();
    const eventSeverityOptions = [
        {name: t("Red"), value: EVENT_SEVERITY.RED},
        {name: t("Yellow"), value: EVENT_SEVERITY.YELLOW},
        {name: t("Green with informational message"), value: EVENT_SEVERITY.GREEN_WITH_INFO}
    ];
    const lastUpdatedAt = event?.lastUpdatedAt;
    const backendEventData: { [key: string]: string | undefined } = event ?
        constructFormData(event) : {};
    const formRef = React.useRef<HTMLFormElement>(null);
    const eventSevFieldRef = React.useRef<KatDropdown.Element>(null);
    const startDateTimeFieldRef = React.useRef<HTMLDivElement>(null);
    const endDateTimeFieldRef = React.useRef<HTMLDivElement>(null);
    const summaryFieldRef = React.useRef<KatInput.Element>(null);
    const descriptionFieldRef = React.useRef<KatTextarea.Element>(null);
    const elementRefsToRequiredMap: Map<React.RefObject<any>, boolean> = new Map<React.RefObject<any>, boolean>([
        [summaryFieldRef, true],
        [descriptionFieldRef, true]
    ]);
    if (section) {
        elementRefsToRequiredMap.set(eventSevFieldRef, true);
        elementRefsToRequiredMap.set(startDateTimeFieldRef, true);
        elementRefsToRequiredMap.set(endDateTimeFieldRef, false);
    }
    const allElementRefs = [...elementRefsToRequiredMap.keys()];
    const requiredElementRefs = [...elementRefsToRequiredMap]
        .filter((kv) => kv[1])
        .map((kv) => kv[0]);

    const getTitle = () => {
        if (section) {
            return `${region} ${section}`
                + (operation ? `.${operation}` : "");
        }
        return title;
    }

    const areRefsValidated = () => {
        // Verify all elements have values and date range is valid
        return section
            && isElementsFromRefsValid(requiredElementRefs)
            && isDateRangeFromRefsValid(startDateTimeFieldRef, endDateTimeFieldRef);
    }

    const constructEventFromForm = () => {
        const formEventData: { [key: string]: string } = getElementsDataFromRefs(allElementRefs);
        return constructEvent(formEventData, region, HEALTH_EVENT,
            section as string, operation as string);
    }

    const handleSubmit = () => {
        if (areRefsValidated()) {
            const event: IEvent = constructEventFromForm();
            if (lastUpdatedAt != undefined) {
                event.lastUpdatedAt = lastUpdatedAt;
            }
            postEventAction(eventDispatch, event);
            resetFormData(formRef);
            setSubmittingStatus(true);
        }
    }

    const handleDelete = () => {
        if (areRefsValidated()) {
            const event: IEvent = constructEventFromForm();
            event.lastUpdatedAt = lastUpdatedAt;
            deleteEventAction(eventDispatch, event);
            resetFormData(formRef);
            setDeletingStatus(true);
        }
    }

    const handleClose = () => {
        resetFormData(formRef);
        setVisible(false);
    }

    return (
        <KatModal className="event-modal"
            visible={true}
            onClose={() => handleClose()}
        >
            <span slot="title">{getTitle()}</span>
            <div className="event-modal-content">
                <form ref={formRef}>
                    {section ?
                        <>
                            <KatDropdown
                                ref={eventSevFieldRef}
                                id={SEVERITY_ID}
                                label={t("Event Severity")}
                                className={FIELD_CLASS_NAME}
                                options={eventSeverityOptions}
                                value={backendEventData[SEVERITY_ID]}
                            />
                            <div className="date-range-container">
                                <DateTimePicker
                                    ref={startDateTimeFieldRef}
                                    id={START_ID}
                                    className={FIELD_CLASS_NAME}
                                    label={t("Start Date Time")}
                                    value={backendEventData[START_ID]}
                                />
                                <DateTimePicker
                                    ref={endDateTimeFieldRef}
                                    id={END_ID}
                                    className={FIELD_CLASS_NAME}
                                    label={t("End Date Time")}
                                    value={backendEventData[END_ID]}
                                />
                            </div>
                        </> : null
                    }
                    <KatInput
                        ref={summaryFieldRef}
                        id={SUMMARY_ID}
                        className={FIELD_CLASS_NAME}
                        label={t("Summary")}
                        type="text"
                        value={backendEventData[SUMMARY_ID]}
                    />
                    <KatTextarea
                        ref={descriptionFieldRef}
                        id={DESCRIPTION_ID}
                        className={FIELD_CLASS_NAME}
                        label={t("Description")}
                        value={backendEventData[DESCRIPTION_ID]}
                    />
                </form>
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton
                    className="cancel-button"
                    variant="secondary"
                    label={t("Cancel")}
                    onClick={() => handleClose()}
                />
                <KatButton
                    form="event-modal-form"
                    className="submit-button"
                    label={t("Submit")}
                    onClick={() => handleSubmit()}
                    loading={isSubmitting}
                    disabled={isDeleting || isSubmitting}
                />
                {lastUpdatedAt ?
                    <KatButton
                        form="event-modal-form"
                        className="delete-button"
                        label={t("Delete")}
                        onClick={() => handleDelete()}
                        loading={isDeleting}
                        disabled={isDeleting || isSubmitting}
                        variant="danger"
                    /> : null}
            </div>
        </KatModal>
    );
}